import React, { useMemo } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { validateField } from '../../../utils/onboardingValidation';
import { FormInput, FormSelect } from '../index';
import styles from './Step1BusinessInfo.module.css';

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export const Step1BusinessInfo: React.FC = () => {
  const { formData, errors, updateFormField, clearError } = useOnboardingStore();

  const stateOptions = useMemo(
    () => US_STATES.map((state) => ({ value: state, label: state })),
    []
  );

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    updateFormField(field, value);
    // Clear error for this field when user starts typing/changing it
    if (errors.has(field as string)) {
      clearError(field as string);
    }
  };

  const handleFieldBlur = (field: string, value: any) => {
    const validation = validateField(field, value);
    // Errors are managed by the store
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.iconBox}>
          <svg
            className={styles.icon}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M4 4a1 1 0 011-1h10a1 1 0 011 1v2h2a1 1 0 110 2h-.5v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8H2.5a1 1 0 110-2H4V4zm3 0v2h6V4H7zm0 4v8h6V8H7z" />
          </svg>
        </div>
        <div>
          <h2 className={styles.title}>
            Business Information
          </h2>
          <p className={styles.subtitle}>
            Tell us about your store
          </p>
        </div>
      </div>

      <div className={styles.formGroup}>
        <FormInput
          label="Store Name"
          name="businessName"
          value={formData.businessName}
          onChange={(value) => handleFieldChange('businessName', value)}
          onBlur={(value) => handleFieldBlur('businessName', value)}
          placeholder="Enter your store name"
          required
          error={errors.get('businessName')}
          helperText="Your business store name"
        />

        <FormInput
          label="LLC Name"
          name="llcName"
          value={formData.llcName || ''}
          onChange={(value) => handleFieldChange('llcName', value)}
          onBlur={(value) => handleFieldBlur('llcName', value)}
          placeholder="Enter LLC name (if applicable)"
          required
          error={errors.get('llcName')}
          helperText="Your Limited Liability Company legal name"
        />

        <FormInput
          label="Store Address"
          name="addressLine1"
          value={formData.addressLine1}
          onChange={(value) => handleFieldChange('addressLine1', value)}
          onBlur={(value) => handleFieldBlur('addressLine1', value)}
          placeholder="123 Main Street"
          required
          error={errors.get('addressLine1')}
          helperText="Your store street address"
        />

        <div className={styles.addressRow}>
          <FormInput
            label="City"
            name="city"
            value={formData.city}
            onChange={(value) => handleFieldChange('city', value)}
            onBlur={(value) => handleFieldBlur('city', value)}
            placeholder="New York"
            required
            error={errors.get('city')}
          />

          <FormSelect
            label="State"
            name="state"
            value={formData.state}
            onChange={(value) => handleFieldChange('state', value)}
            options={stateOptions}
            placeholder="Select state"
            required
            error={errors.get('state')}
          />

          <FormInput
            label="ZIP Code"
            name="zip"
            value={formData.zip}
            onChange={(value) => handleFieldChange('zip', value)}
            onBlur={(value) => handleFieldBlur('zip', value)}
            placeholder="10001 or 10001-1234"
            required
            error={errors.get('zip')}
            helperText="5 or 9 digit ZIP code"
          />
        </div>
      </div>

      {/* Information Box */}
      <div className={styles.infoBox}>
        <div className={styles.infoContent}>
          <div className={styles.infoIcon}>
            <svg
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className={styles.infoTitle}>
              Store Information
            </p>
            <p className={styles.infoText}>
              Please provide accurate store and business information. This will be used to verify your store location
              and complete your registration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
